/**
 * Reads the organization's feature configuration.
 *
 * It sits in shared hooks rather than in a domain because two different domains consume it, and
 * a domain may not import from another.
 */
import { useQuery } from 'react-query';

import { createHeaders } from 'utils/baasRequest';
import { getOrganizationConfigRequest } from 'utils/api/baas/subscription';

export type OrganizationConfig = {
  allowTicketEditor?: boolean;
};

type OrganizationConfigResponse = {
  data: OrganizationConfig;
};

/**
 * Fetches the organization configuration settings.
 *
 * @param organizationUuid - Organization UUID
 * @param storeUuid - Store UUID for the config request
 * @returns Organization config data and state management functions
 */
export const useGetOrganizationConfig = (organizationUuid: string, storeUuid: string) => {
  const { data, isFetching, isFetched, isError, refetch, remove } = useQuery<
    OrganizationConfigResponse,
    Error,
    OrganizationConfig
  >(
    ['get-organization-config', organizationUuid, storeUuid],
    // The underlying request helper is untyped JavaScript, so the response shape is asserted here.
    () =>
      getOrganizationConfigRequest(
        { organizationUuid, storeUuid },
        { headers: createHeaders() },
      ) as Promise<OrganizationConfigResponse>,
    {
      initialData: { data: {} },
      select: (response) => response.data,
      enabled: !!(organizationUuid && storeUuid),
      retry: 0,
    },
  );

  return {
    organizationConfigData: data,
    organizationConfigState: {
      fetching: isFetching,
      fetched: isFetched,
      error: isError,
    },
    getOrganizationConfig: refetch,
    resetGetOrganizationConfig: remove,
  };
};

export default useGetOrganizationConfig;
